import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus, Shield } from 'lucide-react';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { AdjustmentDrawer } from '../components/wallet';

export const Route = createFileRoute('/_authed/system/wallet/adjustments')({
  component: WalletAdjustmentsPage,
});

function WalletAdjustmentsPage() {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const isSuperAdmin = role === 'super_admin';
  const activityPath = useMenuPath('/system/audit');
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!isSuperAdmin) {
    return (
      <ListShell title="Referral balances" description="Super-admin only.">
        <EmptyState
          title="This page is restricted"
          description="Only super admins can record manual wallet ledger adjustments."
          icon={<Shield size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }

  return (
    <ListShell
      title="Referral balances"
      description="Record a manual ledger entry. Every adjustment is audit-logged."
      primaryAction={
        <Button
          onClick={() => setDrawerOpen(true)}
          icon={<Plus size={16} aria-hidden="true" />}
        >
          New adjustment
        </Button>
      }
    >
      <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6">
        <h2 className="font-display text-lg font-semibold">When to use this</h2>
        <ul className="mt-3 space-y-2 text-sm text-[color:var(--color-text-muted)]">
          <li>
            <span className="font-semibold text-[color:var(--color-text)]">
              Admin adjustment:
            </span>{' '}
            Refunds, manual corrections, or one-off goodwill credits.
          </li>
          <li>
            <span className="font-semibold text-[color:var(--color-text)]">
              Clawback debit:
            </span>{' '}
            When a confirmed referral is later invalidated (e.g. the student
            never attended). Use a negative amount.
          </li>
          <li>
            <span className="font-semibold text-[color:var(--color-text)]">
              Referral credit (manual):
            </span>{' '}
            Legacy imports or pre-platform referrals being honoured now. Use
            with care — the normal flow creates these automatically.
          </li>
        </ul>
        <p className="mt-4 text-xs text-[color:var(--color-text-muted)]">
          Adjustments take effect immediately.
          {activityPath ? ` Past adjustments are listed in ${activityPath}.` : ''}
        </p>
        <div className="mt-5">
          <Button
            onClick={() => setDrawerOpen(true)}
            icon={<Plus size={16} aria-hidden="true" />}
          >
            New adjustment
          </Button>
        </div>
      </section>

      <AdjustmentDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </ListShell>
  );
}
